import { Button, Card } from "@prisma/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BarChart3,
  Brain,
  Check,
  ChevronRight,
  Eye,
  FileText,
  FlaskConical,
  Heart,
  Layers,
  Lightbulb,
  ListChecks,
  Plus,
  RefreshCw,
  Sparkles,
  Target,
  Trash2,
  TrendingUp,
  TriangleAlert,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Dialog } from "../../components/Dialog";
import { QueryState } from "../../components/QueryState";
import { api, ApiError, call } from "../../lib/api";
import type { components } from "../../lib/api-types";
import { formatRelative, secondsSince } from "../../lib/format";

type Workspace = components["schemas"]["StrategyWorkspaceOut"];
type Item = components["schemas"]["ItemOut"];
type Hypothesis = components["schemas"]["HypothesisOut"];
type Fact = components["schemas"]["FactOut"];
type ItemKind = components["schemas"]["StrategyItemIn"]["kind"];

const TABS = ["board", "directions", "audience", "competitive", "saved"] as const;
type Tab = (typeof TABS)[number];
const BRAIN_TABS = ["product", "audience", "claims", "history"] as const;
const HYP_FIELDS = ["problem", "desired_state", "primary_motivation", "primary_emotion", "desired_emotional_transition",
  "angle", "hook_direction", "proof_strategy", "offer_role", "cta_direction"] as const;
const KIND = { observation: "HISTORICAL_OBSERVATION", inference: "INFERENCE", recommendation: "RECOMMENDATION",
  hook: "HOOK", emotion: "EMOTIONAL_ANGLE", proof: "PROOF_STRATEGY" } as const;
const ACTIONS = { keep: "keep", explore: "explore", discard: "discard" } as const;
const WS = { open: "openStrategy", refresh: "refreshContext" } as const;

/** Karya Strategy Workspace in context mode (locked screens 02/06, decision O2 layout; doc 04 P7).
 *  Known Fact comes from Seed only; every other card and chip is written by the Creative Producer.
 *  Signals that need learning or a live agent runtime show their honest state. */
export function StrategyWorkspacePage() {
  const { displayId = "" } = useParams();
  const { t } = useTranslation();
  const qc = useQueryClient();
  const key = ["strategy", displayId];
  const q = useQuery({ queryKey: key,
    queryFn: () => call<Workspace>(api.GET("/app/v1/projects/{display_id}/strategy", { params: { path: { display_id: displayId } } })) });
  const [tab, setTab] = useState<Tab>("board");
  const [selected, setSelected] = useState<string | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: key });
  const open = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/strategy/open", { params: { path: { display_id: displayId } } })),
    onSuccess: refresh,
  });
  return (
    <QueryState query={q}>
      {(w) => {
        const hyp = w.board.hypothesis.find((h) => h.id === selected) ?? null;
        return (
          <div className="space-y-6">
            <nav aria-label={t("requests.breadcrumb")} className="flex items-center gap-1.5 text-[13px] text-ink-2">
              <Link to="/projects" className="hover:text-ink">{t("nav.projects")}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <Link to={`/projects/${w.display_id}`} className="hover:text-ink">{w.display_id}</Link>
              <ChevronRight aria-hidden="true" className="size-3.5" />
              <span className="text-ink">{t("karya.strategy")}</span>
            </nav>
            <Hero />
            {w.allowed_actions.includes(WS.open) && (
              <Card className="flex flex-wrap items-center justify-between gap-4 p-5" role="status">
                <p className="text-[14px] text-ink">{t("karya.not_started")}</p>
                <Button variant="primary" disabled={open.isPending} onClick={() => open.mutate()}>{t("projects.start_strategy")}</Button>
              </Card>
            )}
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
              <ContextRow w={w} />
              <ProductBrainPanel w={w} />
            </div>
            <Card className="p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line">
                <div role="tablist" aria-label={t("karya.tabs_label")} className="flex flex-wrap gap-1">
                  {TABS.map((k) => (
                    <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
                      className="-mb-px border-b-2 border-transparent px-4 py-2.5 text-[14px] text-ink-2 hover:text-ink aria-selected:border-[var(--accent-primary)] aria-selected:font-semibold aria-selected:text-ink">
                      {t(`karya.tab_${k}`)}
                    </button>
                  ))}
                </div>
                <SessionLine w={w} onDone={refresh} />
              </div>
              <div className="pt-5">
                {tab === "board" && <Board w={w} selected={selected} onSelect={setSelected} onChange={refresh} />}
                {tab === "saved" && <Saved w={w} />}
                {tab !== "board" && tab !== "saved" && (
                  <p role="status" className="py-10 text-center text-[14px] text-ink-2">
                    <span className="font-semibold text-ink">{t("honest.capability_not_enabled")}</span>
                    {" · "}{t(`karya.tab_${tab}_honest`)}
                  </p>
                )}
              </div>
              {tab === "board" && <ActionBar w={w} hyp={hyp} onChange={refresh} />}
            </Card>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <ChipPanel w={w} kind={KIND.hook} items={w.chips.hook} icon={Lightbulb} titleKey="karya.hooks" numbered onChange={refresh} />
              <ChipPanel w={w} kind={KIND.emotion} items={w.chips.emotional_angle} icon={Heart} titleKey="karya.emotional_angle" pills onChange={refresh} />
              <ChipPanel w={w} kind={KIND.proof} items={w.chips.proof_strategy} icon={BarChart3} titleKey="karya.proof_strategy" checks onChange={refresh} />
            </div>
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Card className="p-5">
                <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ink"><TriangleAlert aria-hidden="true" className="size-5 text-[var(--status-warning)]" />{t("karya.fatigue")}</h2>
                <p className="mt-3 text-[13px] text-ink-2">{t("honest.insufficient_data")} · {t("karya.fatigue_honest")}</p>
              </Card>
              <Card className="p-5">
                <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ink"><TrendingUp aria-hidden="true" className="size-5 text-[var(--status-success)]" />{t("karya.winning_signals")}</h2>
                <p className="mt-3 text-[13px] text-ink-2">{t("honest.insufficient_data")} · {t("dashboard.arrives_with_learning")}</p>
              </Card>
              <Card className="p-5">
                <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ink"><ListChecks aria-hidden="true" className="size-5 text-[var(--status-success)]" />{t("karya.next_steps")}</h2>
                <ol className="mt-3 space-y-1.5 text-[13px] text-ink">
                  {(["1", "2", "3"] as const).map((n) => (
                    <li key={n} className="flex gap-2"><span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-[var(--accent-primary-soft)] text-[11px] font-semibold text-violet-text">{n}</span>{t(`karya.next_step_${n}`)}</li>
                  ))}
                </ol>
              </Card>
            </div>
          </div>
        );
      }}
    </QueryState>
  );
}

function Hero() {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0 flex-1">
        <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-violet-text">{t("karya.mode")}</div>
        <h1 className="mt-1 text-[40px] font-semibold leading-[1.1] tracking-[-0.02em] text-ink">
          {t("karya.title_lead")} <span className="text-gradient">{t("karya.title_em")}</span>
        </h1>
        <p className="mt-2 text-[19px] text-ink-2">{t("karya.subtitle")}</p>
      </div>
      <div className="flex shrink-0 items-center gap-4">
        <p className="max-w-56 text-right text-[17px] italic leading-snug text-ink-2">{t("karya.quote")}</p>
        {/* Karya mark: a gradient glyph, not a persona portrait (decision O6). */}
        <div className="flex items-center gap-3">
          <span aria-hidden="true" className="flex size-14 items-center justify-center rounded-full bg-[image:var(--gradient-primary-button)] text-[var(--text-on-accent)] shadow-[0_6px_24px_var(--accent-primary-soft)]">
            <Sparkles className="size-7" />
          </span>
          <div>
            <div className="text-[20px] font-semibold text-violet-text">{t("nav.karya")}</div>
            <div className="text-[13px] text-ink-2">{t("karya.role")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ContextRow({ w }: { w: Workspace }) {
  const { t } = useTranslation();
  const b = w.brief;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)]">
      <Card className="flex gap-4 p-5 md:col-span-2 2xl:col-span-1">
        <span className="flex w-20 shrink-0 flex-col items-center justify-center gap-1 rounded-[var(--radius-control)] bg-[var(--accent-primary-soft)] p-2 text-center text-[12px] font-semibold text-violet-text">
          <FileText aria-hidden="true" className="size-6" />{t("karya.creative_brief")}
        </span>
        <div className="min-w-0">
          <div className="text-[16px] font-semibold text-ink">{w.product.name} · {t(`requests.objective_${b.objective.toLowerCase()}`)}</div>
          <p className="mt-1 line-clamp-4 text-[13px] text-ink-2">
            {[b.target_audience, b.marketing_insight, b.requested_angle, b.offer].filter(Boolean).join(" · ")}
          </p>
          <Link to={`/requests/${w.display_id}`} className="mt-2 inline-block text-[13px] text-violet-text hover:underline">{t("karya.view_brief")}</Link>
        </div>
      </Card>
      <Card className="flex gap-3 p-5">
        <Layers aria-hidden="true" className="size-6 shrink-0 text-[var(--status-success)]" />
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-violet-text">{t("karya.product_context")}</div>
          <div className="text-[15px] font-semibold text-ink">{w.product.name}</div>
          {w.product_context.facts.length === 0 ? (
            <p className="mt-1 text-[13px] text-ink-2">{t("honest.insufficient_data")}</p>
          ) : (
            <p className="mt-1 text-[13px] text-ink-2">{w.product_context.facts.map((f) => `${t(`karya.fact_${f.key}`, { defaultValue: f.key })}: ${f.value}`).join(" · ")}</p>
          )}
          {w.product_context.is_fixture && <span className="mt-2 inline-block rounded-full bg-elevated px-2 py-0.5 text-[11px] text-ink-2">{t("common.fixture_data")}</span>}
        </div>
      </Card>
      <Card className="flex gap-3 p-5">
        <Users aria-hidden="true" className="size-6 shrink-0 text-[var(--status-info)]" />
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-violet-text">{t("karya.audience_context")}</div>
          <div className="text-[15px] font-semibold text-ink">{w.audience_context.description}</div>
          <p className="mt-1 text-[13px] text-ink-2">{w.audience_context.insight ?? t("requests.not_provided")}</p>
          <span className="mt-2 inline-block text-[11px] text-ink-2">{t("karya.from_brief")}</span>
        </div>
      </Card>
    </div>
  );
}

function SessionLine({ w, onDone }: { w: Workspace; onDone: () => void }) {
  const { t, i18n } = useTranslation();
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/karya/context", { params: { path: { display_id: w.display_id } } })),
    onSuccess: onDone,
  });
  const s = w.session;
  return (
    <div className="flex items-center gap-3 pb-2 text-[12px] text-ink-2">
      {s ? (
        <span role="status">
          {s.status === "SUCCEEDED" ? t("karya.context_mode") : t("karya.runtime_unavailable")}
          {s.created_at && ` · ${t("karya.updated", { when: formatRelative(secondsSince(s.created_at), i18n.language) })}`}
        </span>
      ) : (
        <span>{t("karya.no_session")}</span>
      )}
      {w.allowed_actions.includes(WS.refresh) && (
        <Button variant="ghost" className="h-8 px-2" onClick={() => m.mutate()} disabled={m.isPending}>
          <RefreshCw aria-hidden="true" className="size-4" />{t("karya.refresh_context")}
        </Button>
      )}
    </div>
  );
}

const COLUMNS: { key: "known_fact" | "historical_observation" | "inference" | "hypothesis" | "recommendation";
  icon: LucideIcon; tone: string; bg: string; kind?: ItemKind }[] = [
  { key: "known_fact", icon: FileText, tone: "text-[var(--status-info)]", bg: "bg-[var(--status-info-soft)]" },
  { key: "historical_observation", icon: BarChart3, tone: "text-[var(--status-success)]", bg: "bg-[var(--status-success-soft)]", kind: KIND.observation },
  { key: "inference", icon: Eye, tone: "text-violet", bg: "bg-[var(--accent-primary-soft)]", kind: KIND.inference },
  { key: "hypothesis", icon: FlaskConical, tone: "text-magenta", bg: "bg-[var(--accent-secondary-soft)]" },
  { key: "recommendation", icon: Target, tone: "text-[var(--status-warning)]", bg: "bg-[var(--status-warning-soft)]", kind: KIND.recommendation },
];

function Board({ w, selected, onSelect, onChange }: { w: Workspace; selected: string | null; onSelect: (id: string) => void; onChange: () => void }) {
  const { t } = useTranslation();
  return (
    <section aria-labelledby="analysis-title">
      <h2 id="analysis-title" className="flex items-center gap-2 text-[18px] font-semibold text-ink"><BarChart3 aria-hidden="true" className="size-5 text-violet" />{t("karya.analysis")}</h2>
      <p className="text-[13px] text-ink-2">{t("karya.analysis_sub")}</p>
      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-5">
        {COLUMNS.map((c) => (
          <div key={c.key} className={`flex min-h-48 flex-col rounded-[var(--radius-card)] border border-line ${c.bg} p-3`}>
            <h3 className="flex items-center gap-2 text-[14px] font-semibold text-ink">
              <span className="flex size-7 items-center justify-center rounded-full bg-surface"><c.icon aria-hidden="true" className={`size-4 ${c.tone}`} /></span>
              {t(`karya.label_${c.key}`)}
            </h3>
            <div className="mt-2 flex-1 space-y-2">
              {c.key === "known_fact" && <Facts facts={w.board.known_fact} />}
              {c.key === "hypothesis" && <Hypotheses list={w.board.hypothesis} selected={selected} onSelect={onSelect} />}
              {c.kind && <Items w={w} items={w.board[c.key] as Item[]} onChange={onChange} />}
            </div>
            {c.kind && w.can_edit && <AddItem w={w} kind={c.kind} onDone={onChange} />}
            {c.key === "hypothesis" && w.can_edit && <AddHypothesis w={w} onDone={onChange} />}
            {c.key === "known_fact" && <p className="mt-2 text-[11px] text-ink-2">{t("karya.known_fact_note")}</p>}
          </div>
        ))}
      </div>
    </section>
  );
}

function Facts({ facts }: { facts: Fact[] }) {
  const { t } = useTranslation();
  if (facts.length === 0) return <p className="text-[13px] text-ink-2">{t("honest.insufficient_data")}</p>;
  return (
    <ul className="space-y-2">
      {facts.map((f) => (
        <li key={f.source_ref.reference} className="rounded-[var(--radius-control)] bg-surface p-2 text-[13px] text-ink">
          {t(`karya.fact_${f.key}`, { defaultValue: f.key })}: {f.value}
          <div className="mt-0.5 text-[11px] text-ink-2">{t("karya.source", { source: f.source_ref.source, version: f.source_ref.version })}</div>
        </li>
      ))}
    </ul>
  );
}

function Items({ w, items, onChange }: { w: Workspace; items: Item[]; onChange: () => void }) {
  const { t } = useTranslation();
  const rm = useMutation({
    mutationFn: (i: Item) => call(api.POST("/app/v1/projects/{display_id}/strategy/items/{item_id}/remove", {
      params: { path: { display_id: w.display_id, item_id: i.id } }, body: { row_version: i.row_version } })),
    onSuccess: onChange,
  });
  if (items.length === 0) return <p className="text-[13px] text-ink-2">{t("karya.empty_column")}</p>;
  return (
    <ul className="space-y-2">
      {items.map((i) => (
        <li key={i.id} className="group flex items-start gap-2 rounded-[var(--radius-control)] bg-surface p-2 text-[13px] text-ink">
          <span className="min-w-0 flex-1 whitespace-pre-wrap">{i.body}</span>
          {w.can_edit && (
            <button type="button" aria-label={t("karya.remove_item")} onClick={() => rm.mutate(i)}
              className="shrink-0 rounded p-0.5 text-ink-2 hover:text-ink"><X aria-hidden="true" className="size-3.5" /></button>
          )}
        </li>
      ))}
    </ul>
  );
}

function AddItem({ w, kind, onDone, placeholderKey = "karya.add_card" }: { w: Workspace; kind: ItemKind; onDone: () => void; placeholderKey?: string }) {
  const { t } = useTranslation();
  const [text, setText] = useState("");
  const m = useMutation({
    mutationFn: () => call(api.POST("/app/v1/projects/{display_id}/strategy/items", {
      params: { path: { display_id: w.display_id } }, body: { kind, body: text.trim() } })),
    onSuccess: () => { setText(""); onDone(); },
  });
  const id = `add-${kind}`;
  return (
    <form className="mt-2 flex gap-1.5" onSubmit={(e) => { e.preventDefault(); if (text.trim()) m.mutate(); }}>
      <label htmlFor={id} className="sr-only">{t(placeholderKey)}</label>
      <input id={id} value={text} maxLength={600} onChange={(e) => setText(e.target.value)} placeholder={t(placeholderKey)}
        className="h-8 min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-surface px-2 text-[13px] text-ink placeholder:text-ink-2" />
      <button type="submit" aria-label={t(placeholderKey)} disabled={!text.trim() || m.isPending}
        className="flex size-8 shrink-0 items-center justify-center rounded-[var(--radius-control)] border border-line bg-surface text-ink-2 hover:text-ink disabled:opacity-50">
        <Plus aria-hidden="true" className="size-4" />
      </button>
    </form>
  );
}

function HypStatus({ status }: { status: string }) {
  const { t } = useTranslation();
  const tone: Record<string, string> = {
    KEPT: "bg-[var(--status-success-soft)] text-[var(--status-success)]",
    EXPLORE: "bg-[var(--accent-primary-soft)] text-violet-text",
    DISCARDED: "bg-[var(--status-danger-soft)] text-[var(--status-danger)]",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase ${tone[status] ?? "bg-soft text-ink-2"}`}>{t(`karya.hyp_status_${status.toLowerCase()}`)}</span>;
}

function Hypotheses({ list, selected, onSelect }: { list: Hypothesis[]; selected: string | null; onSelect: (id: string) => void }) {
  const { t } = useTranslation();
  if (list.length === 0) return <p className="text-[13px] text-ink-2">{t("karya.empty_hypotheses")}</p>;
  return (
    <ul className="space-y-2">
      {list.map((h) => (
        <li key={h.id}>
          <button type="button" aria-pressed={selected === h.id} onClick={() => onSelect(h.id)}
            className={`w-full rounded-[var(--radius-control)] border bg-surface p-2 text-left text-[13px] text-ink ${selected === h.id ? "border-[var(--accent-secondary)] shadow-[var(--focus-ring)]" : "border-transparent"} ${h.status === "DISCARDED" ? "opacity-60" : ""}`}>
            <span className="flex items-center justify-between gap-2"><span className="font-medium">{h.name}</span><HypStatus status={h.status} /></span>
            {h.angle && <span className="mt-1 block text-ink-2">{h.angle}</span>}
            {h.desired_emotional_transition && <span className="mt-0.5 block text-[12px] text-ink-2">{h.desired_emotional_transition}</span>}
          </button>
        </li>
      ))}
    </ul>
  );
}

function HypothesisForm({ values, onChange }: { values: Record<string, string>; onChange: (k: string, v: string) => void }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <div className="md:col-span-2">
        <label htmlFor="hyp-name" className="mb-1 block text-[13px] font-medium text-ink-2">{t("karya.hyp_name")}</label>
        <input id="hyp-name" value={values.name ?? ""} maxLength={600} onChange={(e) => onChange("name", e.target.value)}
          className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink" />
      </div>
      {HYP_FIELDS.map((f) => (
        <div key={f}>
          <label htmlFor={`hyp-${f}`} className="mb-1 block text-[13px] font-medium text-ink-2">{t(`karya.hyp_${f}`)}</label>
          <input id={`hyp-${f}`} value={values[f] ?? ""} maxLength={600} onChange={(e) => onChange(f, e.target.value)}
            className="h-10 w-full rounded-[var(--radius-control)] border border-line bg-surface px-3 text-[14px] text-ink" />
        </div>
      ))}
    </div>
  );
}

function ErrorLine({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = error instanceof ApiError ? error.code : "unknown";
  return <p role="alert" className="mt-3 text-[14px] text-[var(--status-danger)]">{t(`error.${code}`, { defaultValue: t("error.unknown") })}</p>;
}

function AddHypothesis({ w, onDone }: { w: Workspace; onDone: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const m = useMutation({
    mutationFn: () => {
      const body: Record<string, string | null> = { name: (values.name ?? "").trim() };
      for (const f of HYP_FIELDS) body[f] = values[f]?.trim() || null;
      return call(api.POST("/app/v1/projects/{display_id}/hypotheses", {
        params: { path: { display_id: w.display_id } }, body: body as components["schemas"]["HypothesisIn"] }));
    },
    onSuccess: () => { setOpen(false); setValues({}); onDone(); },
  });
  return (
    <>
      <Button variant="secondary" className="mt-2 h-8 px-2 text-[13px]" onClick={() => setOpen(true)}>
        <Plus aria-hidden="true" className="size-4" />{t("karya.add_hypothesis")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen} title={t("karya.add_hypothesis")} description={t("karya.hyp_dialog_body")} wide>
        <HypothesisForm values={values} onChange={(k, v) => setValues({ ...values, [k]: v })} />
        <ErrorLine error={m.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!(values.name ?? "").trim() || m.isPending} onClick={() => m.mutate()}>{t("karya.save_hypothesis")}</Button>
        </div>
      </Dialog>
    </>
  );
}

function ModifyHypothesis({ w, hyp, onDone }: { w: Workspace; hyp: Hypothesis; onDone: () => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const initial = (): Record<string, string> => {
    const v: Record<string, string> = { name: hyp.name };
    for (const f of HYP_FIELDS) v[f] = hyp[f] ?? "";
    return v;
  };
  const [values, setValues] = useState<Record<string, string>>(initial);
  const m = useMutation({
    mutationFn: () => {
      const body: Record<string, string | number | null> = { name: (values.name ?? "").trim(), row_version: hyp.row_version };
      for (const f of HYP_FIELDS) body[f] = values[f]?.trim() || null;
      return call(api.PATCH("/app/v1/projects/{display_id}/hypotheses/{hypothesis_id}", {
        params: { path: { display_id: w.display_id, hypothesis_id: hyp.id } }, body: body as components["schemas"]["HypothesisUpdateIn"] }));
    },
    onSuccess: () => { setOpen(false); onDone(); },
  });
  return (
    <>
      <Button variant="ghost" onClick={() => { setValues(initial()); setOpen(true); }}>{t("karya.modify")}</Button>
      <Dialog open={open} onOpenChange={setOpen} title={t("karya.modify_title")} wide>
        <HypothesisForm values={values} onChange={(k, v) => setValues({ ...values, [k]: v })} />
        <ErrorLine error={m.error} />
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
          <Button variant="primary" disabled={!(values.name ?? "").trim() || m.isPending} onClick={() => m.mutate()}>{t("karya.save_hypothesis")}</Button>
        </div>
      </Dialog>
    </>
  );
}

function ActionBar({ w, hyp, onChange }: { w: Workspace; hyp: Hypothesis | null; onChange: () => void }) {
  const { t } = useTranslation();
  const act = useMutation({
    mutationFn: (action: "keep" | "explore" | "discard") => {
      if (!hyp) throw new Error("no hypothesis selected");
      return call(api.POST("/app/v1/projects/{display_id}/hypotheses/{hypothesis_id}/{action}", {
        params: { path: { display_id: w.display_id, hypothesis_id: hyp.id, action } }, body: { row_version: hyp.row_version } }));
    },
    onSuccess: onChange,
  });
  const can = (a: string) => w.can_edit && hyp !== null && hyp.allowed_actions.includes(a);
  const kept = w.board.hypothesis.filter((h) => h.status === "KEPT").length;
  const btn = "inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] border px-5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50";
  return (
    <div className="mt-5 flex flex-wrap items-center justify-end gap-3 border-t border-line pt-4">
      <p className="mr-auto text-[13px] text-ink-2" aria-live="polite">
        {hyp ? t("karya.selected", { name: hyp.name }) : t("karya.select_hint")}
      </p>
      {hyp && w.can_edit && <ModifyHypothesis key={hyp.id + hyp.row_version} w={w} hyp={hyp} onDone={onChange} />}
      <button type="button" disabled={!can(ACTIONS.keep) || act.isPending} onClick={() => act.mutate(ACTIONS.keep)}
        className={`${btn} border-[var(--status-success)] bg-[var(--status-success-soft)] text-[var(--status-success)]`}>
        <Check aria-hidden="true" className="size-4" />{t("karya.keep")}
      </button>
      <button type="button" disabled={!can(ACTIONS.explore) || act.isPending} onClick={() => act.mutate(ACTIONS.explore)}
        className={`${btn} border-[var(--accent-primary)] bg-[var(--accent-primary-soft)] text-violet-text`}>
        <FlaskConical aria-hidden="true" className="size-4" />{t("karya.explore")}
      </button>
      <button type="button" disabled={!can(ACTIONS.discard) || act.isPending} onClick={() => act.mutate(ACTIONS.discard)}
        className={`${btn} border-[var(--status-danger)] bg-[var(--status-danger-soft)] text-[var(--status-danger)]`}>
        <Trash2 aria-hidden="true" className="size-4" />{t("karya.discard")}
      </button>
      <Button variant="ghost" disabled title={t("honest.capability_not_enabled")}>
        <Sparkles aria-hidden="true" className="size-4" />{t("karya.generate_angles")} · {t("honest.capability_not_enabled")}
      </Button>
      {kept > 0 ? (
        <Link to={`/projects/${w.display_id}/plan`}
          className="inline-flex h-10 items-center gap-2 rounded-[var(--radius-control)] bg-[image:var(--gradient-primary-button)] px-5 text-sm font-medium text-[var(--text-on-accent)]">
          {t("karya.create_plan")}<ChevronRight aria-hidden="true" className="size-4" />
        </Link>
      ) : (
        <Button variant="primary" disabled title={t("karya.create_plan_needs_kept")}>{t("karya.create_plan")}</Button>
      )}
      <ErrorLine error={act.error} />
    </div>
  );
}

function ChipPanel({ w, kind, items, icon: Icon, titleKey, numbered, pills, checks, onChange }: {
  w: Workspace; kind: ItemKind; items: Item[]; icon: LucideIcon; titleKey: string;
  numbered?: boolean; pills?: boolean; checks?: boolean; onChange: () => void;
}) {
  const { t } = useTranslation();
  const rm = useMutation({
    mutationFn: (i: Item) => call(api.POST("/app/v1/projects/{display_id}/strategy/items/{item_id}/remove", {
      params: { path: { display_id: w.display_id, item_id: i.id } }, body: { row_version: i.row_version } })),
    onSuccess: onChange,
  });
  const remove = (i: Item): ReactNode => w.can_edit && (
    <button type="button" aria-label={t("karya.remove_item")} onClick={() => rm.mutate(i)} className="rounded p-0.5 text-ink-2 hover:text-ink">
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
  return (
    <Card className="flex flex-col p-5">
      <h2 className="flex items-center gap-2 text-[15px] font-semibold text-ink"><Icon aria-hidden="true" className="size-5 text-violet" />{t(titleKey)}</h2>
      <div className="mt-3 flex-1">
        {items.length === 0 && <p className="text-[13px] text-ink-2">{t("karya.empty_chips")}</p>}
        {pills ? (
          <ul className="flex flex-wrap gap-2">
            {items.map((i) => (
              <li key={i.id} className="inline-flex items-center gap-1 rounded-full bg-[var(--accent-secondary-soft)] px-3 py-1 text-[13px] text-magenta-text">{i.body}{remove(i)}</li>
            ))}
          </ul>
        ) : (
          <ul className="space-y-1.5">
            {items.map((i, n) => (
              <li key={i.id} className="flex items-center gap-2 text-[13px] text-ink">
                {numbered && <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-elevated text-[11px] font-semibold text-ink-2">{n + 1}</span>}
                {checks && <Check aria-hidden="true" className="size-4 shrink-0 text-violet" />}
                <span className="min-w-0 flex-1">{i.body}</span>
                {remove(i)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {w.can_edit && <AddItem w={w} kind={kind} onDone={onChange} placeholderKey={`${titleKey}_add`} />}
    </Card>
  );
}

function Saved({ w }: { w: Workspace }) {
  const { t } = useTranslation();
  const kept = w.board.hypothesis.filter((h) => h.status === "KEPT");
  if (kept.length === 0) return <p className="py-10 text-center text-[14px] text-ink-2">{t("karya.saved_empty")}</p>;
  return (
    <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
      {kept.map((h) => (
        <li key={h.id} className="rounded-[var(--radius-card)] border border-line p-4">
          <div className="flex items-center justify-between"><span className="font-medium text-ink">{h.name}</span><HypStatus status={h.status} /></div>
          <dl className="mt-2 space-y-1 text-[13px]">
            {HYP_FIELDS.filter((f) => h[f]).map((f) => (
              <div key={f} className="flex gap-2"><dt className="w-40 shrink-0 text-ink-2">{t(`karya.hyp_${f}`)}</dt><dd className="text-ink">{h[f]}</dd></div>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}

function ProductBrainPanel({ w }: { w: Workspace }) {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState<(typeof BRAIN_TABS)[number]>("product");
  const pb = w.product_brain;
  return (
    <aside>
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-[16px] font-semibold text-ink"><Brain aria-hidden="true" className="size-5 text-violet" />{t("nav.product_brain")}</h2>
          <Link to="/product-brain" className="text-[13px] text-violet-text hover:underline">{t("dashboard.view_all")}</Link>
        </div>
        <div className="mt-4 rounded-[var(--radius-card)] bg-elevated p-4">
          <div className="text-[18px] font-semibold text-ink">{w.product.name}</div>
          <p className="mt-1 text-[13px] text-ink-2">{t("karya.brain_stub")}</p>
          {pb.is_fixture && <span className="mt-2 inline-block rounded-full bg-surface px-2 py-0.5 text-[11px] text-ink-2">{t("common.fixture_data")}</span>}
        </div>
        <div role="tablist" aria-label={t("karya.brain_tabs")} className="mt-4 flex border-b border-line">
          {BRAIN_TABS.map((k) => (
            <button key={k} type="button" role="tab" aria-selected={tab === k} onClick={() => setTab(k)}
              className="-mb-px flex-1 border-b-2 border-transparent py-2 text-[13px] text-ink-2 aria-selected:border-[var(--accent-primary)] aria-selected:font-semibold aria-selected:text-ink">
              {t(`karya.brain_${k}`)}
            </button>
          ))}
        </div>
        <div className="pt-3 text-[13px]">
          {tab === "product" && (pb.product.length === 0 ? <p className="text-ink-2">{t("honest.insufficient_data")}</p> : (
            <dl className="divide-y divide-[var(--border-default)]">
              {pb.product.map((f) => (
                <div key={f.source_ref.reference} className="flex gap-3 py-2">
                  <dt className="w-32 shrink-0 text-ink-2">{t(`karya.fact_${f.key}`, { defaultValue: f.key })}</dt>
                  <dd className="text-ink">{f.value}</dd>
                </div>
              ))}
            </dl>
          ))}
          {tab === "audience" && <p className="text-ink-2">{t("honest.insufficient_data")} · {t("karya.brain_audience_honest")}</p>}
          {tab === "claims" && <p className="text-ink-2">{pb.claims.count > 0 ? t("karya.brain_claims_n", { count: pb.claims.count }) : t("karya.brain_claims_none")}</p>}
          {tab === "history" && (pb.history.length === 0 ? <p className="text-ink-2">{t("karya.brain_history_none")}</p> : (
            <ul className="space-y-2">
              {pb.history.map((r) => (
                <li key={String(r.display_id)} className="flex justify-between gap-2">
                  <Link to={`/requests/${String(r.display_id)}`} className="text-violet-text hover:underline">{String(r.display_id)}</Link>
                  <span className="truncate text-ink-2">{String(r.requested_angle ?? t(`requests.objective_${String(r.objective).toLowerCase()}`))}</span>
                  <span className="shrink-0 text-ink-2">{formatRelative(secondsSince(String(r.submitted_at)), i18n.language)}</span>
                </li>
              ))}
            </ul>
          ))}
        </div>
      </Card>
    </aside>
  );
}
